import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import type { CampaignNumberPreviewQuery } from '@ooh/contracts';
import { MapPinPopupCard } from './map-pin-popup-card';
import {
  QuickAddOfferPicker,
  type QuickAddClientOption,
  type QuickAddOfferPickerLockedTarget,
  type QuickAddOfferPickerProps,
  buildQuickAddPickerLabels,
  getInitialSelectedDraftId,
  MAP_QUICK_ADD_PICKER_BASE_KEY,
  useQuickAddPickerData,
} from '@ooh/screens';
import { useQuickAddPipeline } from './use-quick-add-pipeline';
import type { SalesUser } from '@ooh/contracts';

interface MapPinPopupCardContainerProps {
  pinId: string | number;
  onClose: () => void;
  canQuickAdd: boolean;
  clientOptions?: QuickAddClientOption[];
  salesUsers?: SalesUser[];
  authUserId?: number | null;
  canAssignOtherSalesperson?: boolean;
  canCreateClient?: boolean;
  isPublicEnabled?: boolean;
  isRoadsEnabled?: boolean;
  nearbyTitle?: string;
  /** Offer-context lock: when set, the quick-add picker targets this offer only. */
  lockedTarget?: QuickAddOfferPickerLockedTarget | null;
  /** Forwarded to the inner QuickAddOfferPicker — see its own doc comment. */
  campaignNumberPreview?: QuickAddOfferPickerProps['campaignNumberPreview'];
  onCampaignNumberPreviewInputChange?: (input: CampaignNumberPreviewQuery | null) => void;
}

export function MapPinPopupCardContainer({
  pinId,
  onClose,
  canQuickAdd,
  clientOptions = [],
  salesUsers = [],
  authUserId,
  canAssignOtherSalesperson,
  canCreateClient,
  isPublicEnabled = false,
  isRoadsEnabled = false,
  nearbyTitle,
  lockedTarget,
  campaignNumberPreview,
  onCampaignNumberPreviewInputChange,
}: MapPinPopupCardContainerProps) {
  const { t } = useTranslation('map');
  const translatePickerLabel = React.useCallback((key: string) => t(key as never), [t]);
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  // Locked-target mode pins the picker to lockedTarget and synthesizes its own
  // single-entry draft, so the drafts list / last-used preselection go unused.
  const isLocked = lockedTarget != null;
  const { draftsData, lastUsedData } = useQuickAddPickerData({ enabled: !isLocked });
  const { confirm, openFullForm, navigateToNewOffer } = useQuickAddPipeline();

  const drafts = draftsData?.drafts ?? [];
  const initialSelectedDraftId = getInitialSelectedDraftId(draftsData, lastUsedData);
  const [selectedDraftId, setSelectedDraftId] = React.useState<string | null>(
    initialSelectedDraftId,
  );

  React.useEffect(() => {
    setSelectedDraftId(initialSelectedDraftId);
  }, [initialSelectedDraftId]);

  const quickAddAction = canQuickAdd ? (
    <Button
      type="button"
      size="xs"
      variant="outline"
      className="w-full"
      onClick={() => setIsPickerOpen(true)}
    >
      {t('pinPopup.quickAdd')}
    </Button>
  ) : undefined;

  return (
    <>
      <MapPinPopupCard
        pinId={pinId}
        onClose={onClose}
        isPublicEnabled={isPublicEnabled}
        isRoadsEnabled={isRoadsEnabled}
        nearbyTitle={nearbyTitle}
        quickAddAction={quickAddAction}
      />
      {isPickerOpen ? (
        <QuickAddOfferPicker
          presentation="modal"
          mediaUnitIds={[pinId]}
          drafts={drafts}
          selectedDraftId={selectedDraftId}
          onSelectDraft={setSelectedDraftId}
          clientOptions={clientOptions}
          salesUsers={salesUsers}
          authUserId={authUserId}
          canAssignOtherSalesperson={canAssignOtherSalesperson}
          canCreateClient={canCreateClient}
          campaignNumberPreview={campaignNumberPreview}
          onCampaignNumberPreviewInputChange={onCampaignNumberPreviewInputChange}
          createDraftMode="inline"
          lockedTarget={lockedTarget}
          onConfirm={confirm}
          onNavigateToNewOffer={navigateToNewOffer}
          onSuccess={() => setIsPickerOpen(false)}
          onCancel={() => setIsPickerOpen(false)}
          onOpenFullForm={(args) => {
            setIsPickerOpen(false);
            openFullForm(args);
          }}
          {...buildQuickAddPickerLabels(translatePickerLabel, MAP_QUICK_ADD_PICKER_BASE_KEY)}
        />
      ) : null}
    </>
  );
}
